import { useCallback } from 'react';
import styled, { css } from 'styled-components';
import { Gap } from '../Gap/Gap';
import { Heading } from '../Heading/Heading';
import { Label } from '../Label/Label';
import { Paragraph } from '../Paragraph/Paragraph';
import { RadioButton } from '../RadioButton/RadioButton';
import { useRadioList } from './RadioListContext';

const RadioListItemElement = styled.div<{
  selected: boolean;
  isCompactMode: boolean;
  disabled: boolean;
  itemSpace: string;
  bottomSpace: string;
}>`
  ${({ theme, isCompactMode, selected, disabled, itemSpace, bottomSpace }) => {
    if (isCompactMode) {
      return css`
        padding: ${itemSpace ? itemSpace : '0'};
        margin-bottom: ${bottomSpace ? bottomSpace : '8px'};
        background-color: transparent;
        white-space: nowrap;
      `;
    } else {
      return css`
        padding: 20px 40px;
        margin-bottom: 30px;
        background-color: ${selected ? theme.vars.newColor.lightGS2 : 'transparent'};
        border: ${selected && !disabled
          ? `2px solid ${theme.vars.newColor.blue1}`
          : `1px solid ${theme.vars.newColor.lightGS1}`};
        &:hover {
          ${selected || disabled ? null : `border: 1px solid ${theme.vars.newColor.blue2};`}
        }
        &:hover input {
          ${selected || disabled ? null : `border: 1px solid ${theme.vars.newColor.blue2};`}
          ${!selected && !disabled ? `background-color: ${theme.vars.newColor.lightGS2};` : null}
        }

        & > .disabled {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          cursor: not-allowed;
          border: 1px solid ${({ theme }) => theme.vars.newColor.lightGS1};
          background-color: ${({ theme }) => theme.vars.newColor.lightGS2};
          opacity: 0.9;
        }
      `;
    }
  }}

  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;

  &[disabled] {
    cursor: not-allowed;
  }

  label[disabled] {
    cursor: not-allowed;
  }
`;

export interface RadioListItemProps extends IntrinsicProps {
  /**
   * required - heading text to display (shown as h3 in default mode. Default font styles are used in compact mode.)
   */
  heading: string;
  /**
   * optional - description text as a paragraph. Description will not be shown in compact mode.
   */
  description?: string;
  /**
   * optional - state to show if option is selected
   * @default false
   */
  selected?: boolean;
  /**
   * required - you will have to use this to manage which item is selected
   */
  onClick: () => void;
  /**
   * required - this must be a unique identifier
   */
  id: string;
  /**
   * If true, the radio button will be disabled.
   */
  disabled?: boolean;
  /**
   * Padding for each Item.
   */
  radioItemSpace?: string;
  bottomSpace?: string;
}

export const RadioListItem = ({
  id,
  heading,
  description,
  selected,
  onClick,
  disabled,
  radioItemSpace,
  bottomSpace,
  ...props
}: RadioListItemProps) => {
  const radioListContext = useRadioList();
  const compacted = radioListContext?.compacted ?? false;

  const handleOnClick = useCallback(() => {
    if (disabled) {
      return;
    }

    onClick();
  }, [disabled, onClick]);

  return (
    <RadioListItemElement
      id={id}
      selected={selected}
      onClick={handleOnClick}
      data-testid="RadioListItem"
      isCompactMode={compacted}
      disabled={disabled}
      itemSpace={radioItemSpace}
      bottomSpace={bottomSpace}
      {...props}
    >
      <div className={disabled ? 'disabled' : null}></div>
      <RadioButton id={id} selected={selected} onClick={handleOnClick} disabled={disabled} {...props} />
      <Gap right={compacted ? 8 : 40} />
      <div>
        {compacted ? (
          <Label
            for={id}
            color="#000000"
            style={{ fontWeight: selected ? 600 : 350 }}
            name={heading}
            disabled={disabled}
          />
        ) : (
          <Heading variation="h3">{heading}</Heading>
        )}
        {!compacted && <Paragraph>{description}</Paragraph>}
      </div>
    </RadioListItemElement>
  );
};
